import {
	useState,
	useMemo,
	useEffect,
	useRef
} from 'react'
import {
	useSelector,
	useDispatch
} from 'react-redux'
import {
	useProgram,
	useProgramEventLinks,
	useEventLinkEditor
} from '../hooks'
import { usePackages } from '../../FinancialsDashboard/hooks'
import { selectTrainerParam } from '../../users/usersSlice'
import OverlayTrigger from 'react-bootstrap/OverlayTrigger'
import Tooltip from 'react-bootstrap/Tooltip'
import Spinner from 'react-bootstrap/Spinner'
import Button from 'react-bootstrap/Button'
import Modal from 'react-bootstrap/Modal'
import Alert from 'react-bootstrap/Alert'
import Form from 'react-bootstrap/Form'
import styles from './ProgramEditor.module.css'
import {envAwarePost} from '../../../util/util'
import useApiStatusEnvAwarePost from '../../../hooks/useApiStatusEnvAwarePost'
import useApiStatusDispatch from '../../../hooks/useApiStatusDispatch'
import useCurrentUser from '../../../hooks/useCurrentUser'
import Select from 'react-select'
import {
    getMessageTemplates,
    getFavoriteDetails
} from "../../ProgramsAddEdit/ProgramsAddEditSlice"
import EventDetails from '../../EventDetails/EventDetails'
import {
	removeProgramPackageLink,
	linkPackageToProgram,
    addProgramAssignments,
    linkEventToProgram,
    editProgramEventLink,
    removeProgramEventLink,
    createProgramEventLinkException,
    addMessagesToProgram,
    deleteProgramMessage,
    deleteProgramMessageGroup,
    updateProgramMessage,
    editProgram,
    updateEventLinkEditor,
    rescheduleProgramEventLink
} from '../programsSlice'
import {
	getEventsLinkedToProgram,
	getEventDetails,
	getEvents,
} from '../../../state/events'
import {
	useFavoriteEvents,
	useEvent
} from '../../../hooks/events'
import AssignModal from '../AssignModal/AssignModal'
import {
	Link,
	useHistory
} from 'react-router-dom'
import {capitalize} from '../../../util/util'

import WorkoutPreview from '../../WorkoutPreview/WorkoutPreview'
import NutritionPreview from '../../NutritionPreview/NutritionPreview'

import {
	useOnDemandCategories,
	useOnDemandVideos
} from '../../OnDemand/hooks'

const UpdateAssignmentModal = ({ show, toggleShow, id }) => {
    const user = useSelector(state => state.users.current_user)
    const isChain = user.account_type === 'chain'
    const dispatch = useDispatch()
    const [assignments, setAssignments] = useState([])
    const [assignmentsStatus, setAssignmentsStatus] = useState('pending')
    const [updateStatus, setUpdateStatus] = useState('idle')
    //mode one of ['init','confirm']
    const [mode, setMode] = useState('init')

    const assignmentsOptions = useMemo(() => assignments.map(a => ({
        label: a.assigned_user_name,
        value: a.id
    })), [assignments])

    const [selectedOptions, setSelectedOptions] = useState([])
    const [applyToAll, setApplyToAll] = useState(false)
    const statusPost = useApiStatusEnvAwarePost()
    useEffect(() => {
        setSelectedOptions([])
        setApplyToAll(false)
        if (!id||isChain) return
        setAssignmentsStatus('pending')
        envAwarePost('api/programs/api/', {
            action: 'getProgramAssignments',
            program_id: id
        }).then(res => {
            setAssignmentsStatus('idle')
            if (res.success === '1') {
                setAssignments(res.data)
            } else {
                alert(res.message)
            }
        })
    }, [id, show])

    const handleApplyUpdates = () => {
        if (!window.confirm('Are you sure that you want to apply edits to this program to all selected assignments?')) return
        setUpdateStatus('pending')
        const postData = {
            action: 'updateProgramAssignments',
            program_id: id
        }
        if (applyToAll) {
            postData.apply_to_all = 1;
        } else {
            postData.assignment_ids = selectedOptions.map(o => o.value)
        }
        statusPost('api/programs/api/', postData, 'program assignments', 'edit').then(() => {
            setUpdateStatus('idle')
            toggleShow()
        })
    }

    return (
        <Modal show={show} onHide={toggleShow} style={{ padding: "20px" }} size="lg">
            <Modal.Header closeButton>
                <Modal.Title>Update program assignments</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {
                    assignmentsStatus === 'pending' ?
                        <Spinner animation="border" size="sm" /> :
                        <>
                            <div className="d-flex">
                                <Select
                                    isDisabled={applyToAll}
                                    className="w-100"
                                    placeholder={applyToAll ? 'Updating all assignments' : 'Select'}
                                    value={applyToAll ? [] : selectedOptions}
                                    options={assignmentsOptions}
                                    onChange={setSelectedOptions}
                                    isMulti
                                />
                                <Button
                                    variant={applyToAll ? "primary" : "outline-primary"}
                                    onClick={() => { setApplyToAll(!applyToAll) }}
                                    className="ml-1"
                                >
                                    All
                                </Button>
                            </div>
                            <Button
                                disabled={(!applyToAll && !selectedOptions.length) || (updateStatus === 'pending')}
                                onClick={handleApplyUpdates}
                                className="mt-2"
                                variant="outline-success"
                                block
                            >
                                <span className="mr-1">Apply program edits to selected client(s)</span>
                                {updateStatus === 'pending' && <Spinner animation="border" size="sm" />}
                            </Button>
                            <small className="text-danger d-block text-center mt-2">
                                Warning: edits applied to the selected program assignments CANNOT BE UNDONE.
                            </small>
                        </>
                }
            </Modal.Body>
        </Modal>
    )
}

const generateWeeklyDays = (weekDays, fromDay, toDay) => {
	//create base value to use when accumulating result
	const result = Array.from(new Set(weekDays))
	let weekIdx = 1
	while (result[result.length - 1] <= toDay) {
		weekDays.forEach(d => {
			result.push(d + weekIdx * 7)
		})
		weekIdx += 1
	}
	return result.filter(d => fromDay <= d && d <= toDay)
}

const dayRange = (start, end, step) => {
	return Array.from(Array.from(Array(Math.ceil((end - start + 1) / step)).keys()), x => start + x * step);
}

const EventLink = ({link,day,disabled,draggable,onDragStart})=>{
	const dispatch = useDispatch()
	const event = useEvent(
		link?.event?.event_type,
		link?.event?.event_id
	)
	if(!event) return null
	return (
		<div 
			onDragStart={onDragStart}
			draggable={draggable}
			className="d-flex align-items-center"
			onClick={e=>{
				if(disabled) return
				e.stopPropagation()
				dispatch(updateEventLinkEditor({
					isNew:false,
					show:true,
					clickedDay:day,
					link:link,
					eventMeta:{
						type:link.event.event_type,
						id:link.event.event_id
					}
				}))
			}}
		>
			<i 
				className="fas fa-circle mr-1" 
				style={{ 
					fontSize:'10px',
					color: {
						workout:'#5E72E4',
						nutrition:'#F66340',
						task:'#3FCE89',
						message:'rgb(67, 205, 239)'
					}[link.event_type]
				}}
			/>
			{link.event_type === 'message'
				? link.message_body
				: event?.note
			}
		</div>
	)
}

const OnDemandVideoLink = ({link,day,disabled,draggable,onDragStart})=>{
	console.log(link)
	const dispatch = useDispatch()
	return (
		<div 
			onDragStart={onDragStart}
			draggable={draggable}
			className="d-flex align-items-center"
			onClick={e=>{
				if(disabled) return
				e.stopPropagation()
				dispatch(updateEventLinkEditor({
					isNew:false,
					show:true,
					clickedDay:day,
					link:link
				}))
			}}
		>
			<i 
				className="fas fa-video mr-1" 
				style={{ 
					fontSize:'10px',
					color: '#eb03fc'
				}}
			/>
			{link.event.name}
		</div>
	)
}

const MessageLink = ({link,day,disabled,draggable,onDragStart})=>{
	const dispatch = useDispatch()
	return (
		<div 
			onDragStart={onDragStart}
			draggable={draggable}
			className="d-flex align-items-center"
			onClick={e=>{
				if(disabled) return
				e.stopPropagation()
				dispatch(updateEventLinkEditor({
					isNew:false,
					show:true,
					clickedDay:day,
					link:link,
				}))
			}}
		>
			<i 
				style={{ color: '#43CDEF' , fontSize: '11px' }}
				className="fas fa-comment-dots mr-1" 
			/>
			{link.message_body}
		</div>
	)
}

const OnDemandVideoSelector = ({categoryId,selected,onSelect})=>{
	const {status,videos} = useOnDemandVideos(categoryId)
	return (
		<div>
			<strong>
				Video
			</strong>
			<Form.Control 
				as="select"
				value={selected}
				onChange={e=>{
					onSelect(e.target.value)
				}}
			>
				<option value="none">Select a Video</option>
				{videos.map(video=>(
					<option value={video.id}>
						{video.name} ({video.time})
					</option>
				))}
			</Form.Control>
		</div>
	)
}

const OnDemandSelector = ({selected,onSelect,defaultCategory='none'})=>{
	const {status,categories} = useOnDemandCategories()
	const [category,setCategory] = useState(defaultCategory)
	useEffect(()=>{
		setCategory(defaultCategory)
	},[defaultCategory])
	return (
		<div>
			<strong>Category</strong>
			<Form.Control 
				as="select"
				value={category}
				onChange={e=>{
					setCategory(e.target.value)
				}}
			>
				<option value="none">Select a Category</option>
				{categories.map(category=>(
					<option value={category.id}>
						{category.name}
					</option>
				))}
			</Form.Control>
			{category!=='none'&&
				<OnDemandVideoSelector categoryId={category} selected={selected} onSelect={onSelect}/>
			}
		</div>
	)
}

const EventLinkEditModal = ({programId,programLength})=>{
	const user = useCurrentUser()
	const dispatch = useDispatch()
	const {isNew,link,eventMeta,clickedDay,show} = useEventLinkEditor()
	const favoriteDetails = useEvent(
		eventMeta?.type,
		eventMeta?.id,
		//passing true forces the items like 
		//foods/exercises to be loaded
		true
	)

	const history = useHistory()

	const trainerParam = useSelector(selectTrainerParam)

	const [onDemandVideoCategory,setOnDemandVideoCategory] = useState('none')
	const [selectedEvent,setSelectedEvent] = useState(null)
	const [selectedVideoId,setSelectedVideoId] = useState(null)

	const [selectedType,setSelectedType] = useState(isNew?'workout':link.event_type)
	const favoriteEvent = useEvent(selectedEvent?.type,selectedEvent?.value)
	
	//note the next update for programs will subsume messages into the recurrence and 
	const isMessage = selectedType === 'message'

	const [addingEvent,setAddingEvent] = useState(false)
	const handleAddEvent = (eventType)=>{
        setAddingEvent(true)
        envAwarePost('api/events/',{
            action: 'createEvent',
            is_favorite: 1,
            event_type: eventType,
            date: "1969-12-31",
            title: '',
            client_id:user.user_id_number
        }).then(res=>{
            setAddingEvent(false)
            const event_id = res.data[`${eventType}_id`]
            const client_id = user.user_id_number
            const editorRoute = `/eventEditor/?event_id=${event_id}&event_type=AddOrEdit&eventType=${capitalize(eventType)}&client=favorite&origin=program${programId}&${trainerParam}`
            
            history.push(editorRoute)
        })
    }

	const handleTypeSelect = e => {
		setSelectedEvent(null)
		setSelectedVideoId(null)
		setShowDetails(false)
		setSelectedType(e.target.value)
	}
	
	const [startDay,setStartDay] = useState(isNew?clickedDay:link.start_day)
	const [endDay,setEndDay] = useState(isNew?clickedDay:link.end_day)
	const [freq,setFreq] = useState(isNew?'none':link.freq)
	const [interval,setInterval] = useState(isNew?1:link.interval)
	const [customInterval,setCustomInterval] = useState("")
	const [sendAt, setSendAt] = useState("")

	const handleFreqSelect = e => {
		const newFreq = e.target.value
		setStartDay(isNew?clickedDay:link.start_day)
		setFreq(newFreq)
		//if switching from a single day event with no repeat 
		//make sure to use the program length as the end day
		//if this is not done then the end day will default to
		//being the same as the start day
		setEndDay(
			(isNew||(newFreq!=='none'&&link.start_day===link.end_day))
			? programLength
			: link.end_day
		)
	}

	const [checkboxArr, setCheckboxArr] = useState(
		isNew
		? []
		: link.week_days?.split(',').map(v=>parseInt(v))
	)
	
	const handleWeeklyDayCheck = (e) => {
		const val = parseInt(e.target.value)
		if (e.target.checked) {
			setCheckboxArr([...checkboxArr, val])
		} else {
			setCheckboxArr([...checkboxArr].filter(v => v !== val))
		}
	}


	useEffect(()=>{
		if (!isNew) {
			if (selectedType==='message'||link.event_type==='message') {
				setSelectedType('message')
				setMessageBody(link.message_body)
				setSendAt(link.send_at)
				setStartDay(clickedDay)
			} else {
				const isSingleDay = link.start_day === link.end_day
				setFreq(isSingleDay?'none':link.freq)
				setStartDay(link.start_day)
				setEndDay(link.end_day)
				if(link.event_type==='on_demand_video'){
					setSelectedType('on_demand_video')
					setOnDemandVideoCategory(link.event.category_id)
					setSelectedVideoId(link.event.id)
				}
				else{
					setSelectedType(link.event.event_type)
					setSelectedEvent({
						label:link.event.note,
						value:link.event.event_id,
						type:link.event.event_type
					})
				}
				setInterval(link.interval)
				setCheckboxArr(link.week_days?.split(',').filter(Boolean).map(v=>parseInt(v)))
			}
			
		} else {
			setFreq('none')
			setStartDay(clickedDay)
			setEndDay(clickedDay)
			setSelectedType('workout')
			setSelectedEvent(null)
			setInterval(1)
			setCheckboxArr([])
			setSendAt('')
		}
	},[link,clickedDay])

	useEffect(()=>{
		setSelectedEvent(
			(!isNew||Boolean(eventMeta))
			? {label:favoriteDetails.title,value:favoriteDetails.event_id,type:favoriteDetails.event_type}
			: null
		)
	},[favoriteDetails])

	useEffect(()=>{
		dispatch(getEvents({
			event_type:'workout',
			client_id:user.user_id_number,
			filter:'favorite'
		}))
		dispatch(getEvents({
			event_type:'task',
			client_id:user.user_id_number,
			filter:'favorite'
		}))
		dispatch(getEvents({
			event_type:'nutrition',
			client_id:user.user_id_number,
			filter:'favorite'
		}))
	},[])

	const onHide = ()=>{
		setShowDetails(false)
		dispatch(updateEventLinkEditor({
			show:false,
			link:null,
			eventMeta:null,
			clickedDay:1,
			isNew:true
		}))
	}

	const eventTypeMap = {
		workout: useFavoriteEvents('workout'),
		task: useFavoriteEvents('task'),
		nutrition: useFavoriteEvents('nutrition'),
		message: [],
		on_demand_video: []
	}

	const [status,setStatus] = useState('idle')

	const handleSave = ()=>{
		setStatus('pending')
		const isSingleDay = freq === 'none'
		if(selectedType==='message'||link?.event_type==='message'){
			let days
			if (freq == "daily") {
				days = dayRange(parseInt(clickedDay), endDay, interval)
			} else if (freq == "none") {
				days = [parseInt(clickedDay)]
			} else if (freq == "weekly") {
				days = generateWeeklyDays(checkboxArr, parseInt(clickedDay), parseInt(endDay))
			}
			if (isNew) {
				dispatch(addMessagesToProgram({
					program_id:programId,
					program_id:programId,
					message_body:messageBody,
					send_at:sendAt,
					days
				})).then(()=>{
					setStatus('idle')
					onHide()
				})
			} else {
				dispatch(updateProgramMessage({
					program_id:programId,
					day:startDay,
					program_message_id:link.program_message_id,
					message_body:messageBody,
					send_at:sendAt
				})).then(()=>{
					setStatus('idle')
					onHide()
				})
			}
		}else if(isNew){
			dispatch(linkEventToProgram({
				program_id:programId,
				event_type:selectedType,
				event_id:(
					selectedType === 'on_demand_video'
					? selectedVideoId
					: selectedEvent.value
				),
				start_day:startDay,
				end_day:isSingleDay?startDay:endDay,
				freq:isSingleDay?'daily':freq,
				interval:isSingleDay?1:(interval==='custom'?customInterval:interval),
				week_days:checkboxArr.join(',')
			})).then(()=>{
				setStatus('idle')
				onHide()
			})
		}else{
			dispatch(editProgramEventLink({
				program_id:programId,
				id:link.link_id,
				update:{
					event_type:selectedType,
					event_id:(
						selectedType === 'on_demand_video'
						? selectedVideoId
						: selectedEvent.value
					),
					start_day:startDay,
					end_day:isSingleDay?startDay:endDay,
					freq:isSingleDay?'daily':freq,
					interval:isSingleDay?1:(interval==='custom'?customInterval:interval),
					week_days:checkboxArr.join(',')
				}
			})).then(()=>{
				setStatus('idle')
				onHide()
			})
		}
	}

	const handleRemove = ()=>{
		if (!window.confirm('Are you sure you want to remove all instances of this event?')) return 
		setStatus('pending')
		if (isMessage) {
			dispatch(deleteProgramMessageGroup({
				program_id:programId,
				program_message_group_id:link.program_message_group_id
			})).then(()=>{
				setStatus('idle')
				onHide()
			})
		} else {
			dispatch(removeProgramEventLink({
				program_id:programId,
				id:link.link_id
			})).then(()=>{
				setStatus('idle')
				onHide()
			})
		}
	}

	const handleRemoveFromDay = ()=>{
		if(!window.confirm('Are you sure you want to remove this event?')) return 
		setStatus('pending')
		if (isMessage) {
			dispatch(deleteProgramMessage({
				program_id:programId,
				program_message_id:link.program_message_id
			})).then(()=>{
				setStatus('idle')
				onHide()
			})
		} else {
			dispatch(
				createProgramEventLinkException({
					program_id:programId,
					link_id:link.link_id,
					day:clickedDay
				})
			).then(()=>{
				setStatus('idle')
				onHide()
			})
		}
	}

	//handle state for message input
	//selections
	const messageRef = useRef()

	const [selectionStart, setSelectionStart] = useState(0)

	messageRef?.current?.addEventListener('keyup',e=>{
		setSelectionStart(e.target.selectionStart)
	})

	const [showMessageTemplates,setShowMessageTemplates] = useState(false)

	const addAutoMessageTag = (tagType) => {
		const tag = `[${tagType}]`;
		const bodyUpdate = `${messageBody.substring(0, selectionStart)}${tag}${messageBody.substring(selectionStart)}`
		setSelectionStart(selectionStart + tag.length)
		setMessageBody(bodyUpdate)
		messageRef.current.focus()
	}

	const [showDetails, setShowDetails] = useState(false)

	const [favoriteDetailsLoading, setFavoriteDetailsLoading] = useState(false)
	const showFavoriteDetails = (eventId) => {
		setFavoriteDetailsLoading(true)
		dispatch(getEventDetails({
			event_id: eventId ? eventId : selectedEvent.value,
			event_type:selectedEvent.type,
			include_event_items:1
		})).then(res => {
			setFavoriteDetailsLoading(false)
		})
			
	}

	const templates = useSelector(
		state => state.programEvents?.messageTemplates
	)

	const [messageBody, setMessageBody] = useState("")

	return (
		<Modal show={show} onHide={onHide}>
			<Modal.Header closeButton>
				<Modal.Title>{isNew?'Add':'Update'} Item</Modal.Title>
			</Modal.Header>
			<Modal.Body>
				<small>Item Type</small>
				<Form.Control 
					onChange={handleTypeSelect}
					value={selectedType} 
					as="select"
				>
					<option value="workout">Workout</option>
					<option value="task">Task</option>
					<option value="nutrition">Nutrition</option>
					<option value="message">Message</option>
					<option value="on_demand_video">On Demand Video</option>
				</Form.Control>
				{(selectedType!=='message'&&selectedType!=='on_demand_video')&&
					<Select 
						isClearable
						className="mt-2"
						value={selectedEvent}
						onChange={(selectedEvent)=>{
							setShowDetails(false)
							setSelectedEvent(selectedEvent)
						}}
						options={eventTypeMap[selectedType].map(
							e=>({
								value:e.event_id,
								label:e.note,
								type:e.event_type
							})
						)}
					/>
				}
				{selectedType==='message'&&
					<div className="my-1">
						<small className="d-block">Message Body</small>
						<div>
							<Button 
								onClick={() => addAutoMessageTag("fullName")}
								variant="outline-primary" size="sm" className="mr-1">
								Full Name
							</Button>
							<Button 
								onClick={() => addAutoMessageTag("firstName")}
								variant="outline-primary" size="sm" className="mr-1">
								First Name
							</Button>
							<OverlayTrigger
								placement="right"
								overlay={
									<Tooltip id="message">
										Replaces [clientName] with recipient's user name
									</Tooltip>
								}
							>
								<i class="fas fa-info-circle"></i>
							</OverlayTrigger>
						</div>
						{!showMessageTemplates?
							<>
								<Form.Control 
									className="mt-2"
									ref={messageRef}
									placeholder="Optional"
									as="textarea"
									rows={2}
									value={messageBody}
									onClick={e => {
										setSelectionStart(e.target.selectionStart)
									}}
									onChange={(e) => setMessageBody(e.target.value)}
								/>
								<Button 
									onClick={() => { dispatch(getMessageTemplates()); setShowMessageTemplates(prev => !prev) }}
									variant="link"
								>
									Use Previous Message
								</Button>
							</>
							:
							<Form.Group>
								<Form.Label>
									Use Previous Message
								</Form.Label>
								<Form.Control
									as="select"
									onChange={(e) => { setMessageBody(e.target.value); setShowMessageTemplates(false) }}>

									<option>Select Message...</option>
									{
										templates?.map(template => (
											<option value={template?.message_body}>
												{template?.message_body}
											</option>
										))
									}
								</Form.Control>

							</Form.Group>

						}
						<Form.Group>
							<Form.Label>
								Time
							</Form.Label>
							<Form.Control
								value={sendAt}
								as="select"
								onChange={(e) => setSendAt(e.target.value)}
							>
								<option>Select...</option>
								<option value={"00:00:00"}>12:00 AM</option>
								<option value={"01:00:00"}>1:00 AM</option>
								<option value={"02:00:00"}>2:00 AM</option>
								<option value={"03:00:00"}>3:00 AM</option>
								<option value={"04:00:00"}>4:00 AM</option>
								<option value={"05:00:00"}>5:00 AM</option>
								<option value={"06:00:00"}>6:00 AM</option>
								<option value={"07:00:00"}>7:00 AM</option>
								<option value={"08:00:00"}>8:00 AM</option>
								<option value={"09:00:00"}>9:00 AM</option>
								<option value={"10:00:00"}>10:00 AM</option>
								<option value={"11:00:00"}>11:00 AM</option>
								<option value={"12:00:00"}>12:00 PM</option>
								<option value={"13:00:00"}>1:00 PM</option>
								<option value={"14:00:00"}>2:00 PM</option>
								<option value={"15:00:00"}>3:00 PM</option>
								<option value={"16:00:00"}>4:00 PM</option>
								<option value={"17:00:00"}>5:00 PM</option>
								<option value={"18:00:00"}>6:00 PM</option>
								<option value={"19:00:00"}>7:00 PM</option>
								<option value={"20:00:00"}>8:00 PM</option>
								<option value={"21:00:00"}>9:00 PM</option>
								<option value={"22:00:00"}>10:00 PM</option>
								<option value={"23:00:00"}>11:00 PM</option>
							</Form.Control>

							<small>
								These are sent to clients in their timezone.
								<OverlayTrigger
									placement="right"
									overlay={
										<Tooltip id="message">
										Ex: A morning reminder will be sent in the morning of each recipient whether they are in Australia or Europe
										</Tooltip>
									}
								>
									<i class="fas fa-info-circle"></i>
								</OverlayTrigger>

							</small>
						</Form.Group>
					</div>
				}
				{(selectedType!=='message'&&selectedType!=='on_demand_video')&&
					<div className="my-1">
						{!Boolean(selectedEvent) ? 
							<Button
								disabled={addingEvent}
								onClick={handleAddEvent.bind(null,selectedType)}
								variant="link"
							>
								Create New {capitalize(selectedType)} {addingEvent&&<Spinner animation="border" size="sm"/>}
							</Button>
							: 
							<div className="d-flex">
								<Link
									className="mr-2"
									to={
										`/eventEditor/?event_id=${selectedEvent.value}&event_type=AddOrEdit&eventType=${selectedType}&client=favorite&origin=program${programId}&${trainerParam}`
									}
								>
									<p className="text-primary" style={{ cursor: "pointer", fontWeight: "bold" }}>
										Edit {capitalize(selectedType)}
									</p>
								</Link>
								<div> |  </div>
								<p 
									className="text-primary ml-2" style={{ cursor: "pointer", fontWeight: "bold" }}
									onClick={() => { 
										if(!showDetails)showFavoriteDetails()
										setShowDetails((prev) => !prev) 
									}}
								>
									{

										(showDetails == false) ? (
											<>
												View {capitalize(selectedType)} Details
											</>
										) : (
											<>
												Hide {capitalize(selectedType)} Details
											</>
										)

									}

								</p>


							</div>
						}

						{(showDetails && !favoriteDetailsLoading)&&
							//auto invoking function to use switch
							(()=>{
								switch(favoriteEvent.event_type){
									case "workout": return <WorkoutPreview workout={favoriteEvent}/>
									case "nutrition": return <NutritionPreview nutrition={favoriteEvent}/>
									case "task": return <div>{favoriteEvent.notes}</div>
								}
							})()
						}


						{showDetails && favoriteDetailsLoading &&
							<Spinner animation="border" role="status" variant="primary" ></Spinner>
						}
					</div>
				}
				{selectedType==='on_demand_video'&&
					<OnDemandSelector 
						defaultCategory={onDemandVideoCategory}
						selected={selectedVideoId} 
						onSelect={setSelectedVideoId}
					/>
				}
				<div>
					<small>Repeat</small>
					<Form.Control 
						onChange={ handleFreqSelect } 
						value={freq}
						as="select"
					>
						<option value="none">None</option>
						<option value="daily">Daily</option>
						<option value="weekly">Weekly</option>
					</Form.Control>
					{freq==='none'&&
						<div>
							<small>Day</small>
							<Form.Control 
								onChange={
									e => { setStartDay(e.target.value) }
								}
								value={startDay}
							/>
						</div>
					}
					{['daily','weekly'].includes(freq)&&
						<div>
							<small>Repeat From Day</small>
							<Form.Control
								onChange={e=>{ setStartDay(e.target.value) }}
								value={startDay}
							/>
							<small>Until Day</small>
							<Form.Control
								onChange={e=>{ setEndDay(e.target.value) }}
								value={endDay}
							/>
						</div>
					}
					{freq==='daily'&&
						<div>
							<small>Repeat Frequency </small>
							<Form.Control
								as="select"
								onChange={(e) => {
									setInterval(e.target.value)
								}}
							>
								<option value="1">Every Day</option>
								<option value="2">Every 2nd Day</option>
								<option value="3">Every 3rd Day</option>
								<option value="4">Every 4th Day</option>
								<option value="5">Every 5th Day</option>
								<option value="6">Every 6th Day</option>
								<option value="7">Every 7th Day</option>
								<option value="8">Every 8th Day</option>
								<option value="9">Every 9th Day</option>
								<option value="10">Every 10th Day</option>
								<option value="custom">Custom</option>
							</Form.Control>
						</div>
					}
					{(freq==='daily'&&interval==='custom')&&
						<div>
							<small>Custom Repeat Frequency</small>
							<Form.Control 
								onChange={e=>{ setCustomInterval(e.target.value) }} 
								value={customInterval} 
							/>
						</div>
					}
					{freq==='weekly'&&
						<div>
							<fieldset>
								<Form.Group>
									<Form.Check
										type="checkbox"
										label="1st Day"
										name="weeklyCheckbox"
										id="1Day"
										value={1}
										checked={checkboxArr.includes(1)}
										onChange={handleWeeklyDayCheck}
									/>
									<Form.Check
										type="checkbox"
										label="2nd Day"
										name="weeklyCheckbox"
										id="2Day"
										value={2}
										checked={checkboxArr.includes(2)}
										onChange={handleWeeklyDayCheck}
									/>
									<Form.Check
										type="checkbox"
										label="3rd Day"
										name="weeklyCheckbox"
										id="3Day"
										value={3}
										checked={checkboxArr.includes(3)}
										onChange={handleWeeklyDayCheck}
									/>
									<Form.Check
										type="checkbox"
										label="4th Day"
										name="weeklyCheckbox"
										id="4Day"
										value={4}
										checked={checkboxArr.includes(4)}
										onChange={handleWeeklyDayCheck}
									/>
									<Form.Check
										type="checkbox"
										label="5th Day"
										name="weeklyCheckbox"
										id="5Day"
										value={5}
										checked={checkboxArr.includes(5)}
										onChange={handleWeeklyDayCheck}
									/>
									<Form.Check
										type="checkbox"
										label="6th Day"
										name="weeklyCheckbox"
										id="6Day"
										value={6}
										checked={checkboxArr.includes(6)}
										onChange={handleWeeklyDayCheck}
									/>
									<Form.Check
										type="checkbox"
										label="7th Day"
										name="weeklyCheckbox"
										id="7Day"
										value={7}
										checked={checkboxArr.includes(7)}
										onChange={handleWeeklyDayCheck}
									/>
								</Form.Group>
							</fieldset>
						</div>
					}
				</div>
				<div className="text-right mt-2">
					{!isNew&&
						<>
							<Button
								disabled={status==='pending'}
								onClick={handleRemoveFromDay}
								variant="outline-danger"
								className="mr-2"
								size="sm"
							>
								Remove From Day {clickedDay}
							</Button>
							<Button
								disabled={status==='pending'}
								onClick={handleRemove}
								variant="danger"
								className="mr-2"
								size="sm"
							>
								Remove All
							</Button>
						</>
					}
					<Button
						disabled={status==='pending'}
						onClick={handleSave}
						variant="outline-primary"
						size="sm"
					>
						Save
					</Button>
					{status==='pending'&&<Spinner className="ml-1" animation="border" size="sm"/>}
				</div>
			</Modal.Body>
		</Modal>
	)
}

const ProgramEditor = () => {
	const trainerParam = useSelector(selectTrainerParam)
	const dispatch = useDispatch()
	const statusDispatch = useApiStatusDispatch()
	const params = new URLSearchParams(window.location.search)
	const id = params.get('id')
	const event_id = params.get('event_id')
	const event_type = params.get('event_type')
	const event_title = params.get('event_title')
	const defaultToEvent = (Boolean(event_id)&&Boolean(event_type)&&Boolean(event_title))
	const {status:programStatus,program} = useProgram(id)
	const {status:eventLinksStatus,links,weeks} = useProgramEventLinks(id)

	const [dragData,setDragData] = useState(null)

	const handleDrop = (tgtDay) => {
		const {link,day:srcDay} = dragData
		if ( link.event_type === "message" ) {
           dispatch(updateProgramMessage({
				program_id:id,
				day:tgtDay,
				program_message_id:link.program_message_id,
				message_body:link.message_body,
				send_at:link.send_at
			}))
        } else {
        	//sends a request for multiple actions
        	//thus some of these fields are used by different
        	//actions
        	dispatch(rescheduleProgramEventLink({
        		// used by all actions
        		program_id:id,
        		// Data for creating an exception 
        		// (ie hiding the original instance on this date)
        		link_id:link.link_id,
        		day:srcDay,
        		// Data for creating a new event link on the tgt day
        		start_day:tgtDay,
        		end_day:tgtDay,
        		freq:'daily',
        		interval:1,
        		event_type:link.event_type,
        		event_id:link.event.event_id
        	}))
        }
	}

	const {packages} = usePackages()

	useEffect(()=>{
		dispatch(getEventsLinkedToProgram(id))
	},[])

	const user = useCurrentUser()
	const isOwner = user?.user_id_number == program?.user_id_number
	const isChain = user.account_type === 'chain'

	const [showUpdateAssignmentModal, setShowUpdateAssignmentModal] = useState(false)
	const toggleUpdateAssignmentModal = () => {
		setShowUpdateAssignmentModal(!showUpdateAssignmentModal)
	}

	const [selectedEventLink,setSelectedEventLink] = useState(null)
	const [showUpdateEventLinkModal, setShowUpdateEventLinkModal] = useState(defaultToEvent)
	const toggleUpdateEventLinkModal = () => {
		setShowUpdateEventLinkModal(!showUpdateEventLinkModal)
	}

	const [clickedDay,setClickedDay] = useState(1)

	const handleShowAssign = () => {
		dispatch(addProgramAssignments(parseInt(id)))
	}

	const handleUpdate = update => {
		dispatch(editProgram({program_id:id,update}),'program','edit')
	}

	return (
		<div className="pt-3 px-2 px-md-3">
			{programStatus!=='idle'?
				<div className="p-3 d-flex justify-content-center align-items-center">
					<span className="mr-2">Loading program details</span>
					<Spinner animation="border" size="sm"/>
				</div>
				:
				<div className="mb-2">
					<div className="mb-2" style={{ fontSize: "18px" }}>
						<Link to={`/programs?${trainerParam}`}>Program List</Link> / {program.programName}
					</div>
					{!isOwner &&
						<Alert variant="info">
							<i className="fas fa-info-circle mr-2" />
							<span className="text-dark">
								This is a gym program and can only be edited by the gym owner.
								{!isChain && <strong className="ml-2">You can still assign this program to clients</strong>}
							</span>
						</Alert>
					}
					<div className="d-flex align-items-end">
						<div className="w-100 mr-2">
							<strong>Program Name</strong>
							<Form.Control 
								onBlur={e=>{
									handleUpdate({programName:e.target.value})
								}}
								defaultValue={program.programName}
								placeholder="Program name"
								disabled={!isOwner}
								className="mb-1"
								size="sm"
							/>
						</div>
						<div style={{width:'160px'}}>
							<strong>Length (days)</strong>
							<Form.Control 
								onBlur={ e => {
									handleUpdate({programLength:e.target.value})
								}}
								defaultValue={program.programLength}
								disabled={!isOwner}
								placeholder="Program length (days)"
								className="mb-1"
								type="number"
								size="sm"
							/>
						</div>
					</div>
					<strong>Program Notes</strong>
					<Form.Control 
						onBlur={e=>{
							handleUpdate({programNotes:e.target.value})
						}}
						defaultValue={program.programNotes}
						disabled={!isOwner}
						placeholder="Notes"
						className="mb-1"
						as="textarea"
						size="sm"
					/>
					<strong>
						Introductory Message (optional)
						<OverlayTrigger overlay={
							<Tooltip>
								This message is sent when the program is first assigned. 
							</Tooltip>
						}>
							<i className="ml-1 fa fa-info-circle"/>
						</OverlayTrigger>
					</strong>
					<Form.Control 
						onBlur={e=>{
							handleUpdate({intro_message:e.target.value})
						}}
						defaultValue={program.intro_message}
						disabled={!isOwner}
						placeholder="Introductory Message"
						className="mb-1"
						as="textarea"
						size="sm"
						rows={5}
					/>
					<strong>
						Linked Packages 
						<OverlayTrigger overlay={
							<Tooltip>
								When a client purchases a linked package the program is automatically assigned at a date of their choosing.
							</Tooltip>
						}>
							<i className="ml-1 fa fa-info-circle"/>
						</OverlayTrigger>
					</strong>
					<Select 
						isDisabled={!isOwner}
						onChange={(value,payload)=>{
							if ( payload.action === 'select-option' ) {
								dispatch(linkPackageToProgram({
									program_id:id,
									package_id:payload.option.value,
									package_name:payload.option.label
								}))
							}
							if ( payload.action === 'remove-value') {
								dispatch(removeProgramPackageLink({
									program_id:id,
									package_id:payload.removedValue.value
								}))
							}
						}}
						value={(program?.packages || []).map(p=>({
							value:p.package_id,
							label:p.package_name
						}))}
						options={
							packages.map(
								p => (
									{
										label:p.package_name,
										value:p.package_id
									}
								)
							)//end map
						}//end options
						isMulti
					/>
					<div className="d-flex justify-content-between align-items-end flex-wrap">
						{(isOwner&&!isChain) &&
							<div className="mr-auto mb-2 mb-md-0">
								<small className="text-danger">
									<strong>Note</strong>: edits are NOT automatically applied to existing program assignments.
								</small>
								<br />
								<Button
									onClick={toggleUpdateAssignmentModal}
									variant="outline-dark"
									size="sm"
								>
									Open options for updating existing assignments
								</Button>
							</div>
						}
						{!isChain&&
							<Button
								onClick={handleShowAssign}
								variant="outline-success"
								className="mt-2"
								size="sm"
							>
								Assign To Client
							</Button>
						}
					</div>
				</div>
			}
			{eventLinksStatus!=='idle'?
				<div className="p-3 d-flex justify-content-center align-items-center">
					<span className="mr-2">Loading program events</span>
					<Spinner animation="border" size="sm"/>
				</div>
				:
				<div>
					{isOwner &&
						<div style={{ marginTop: "10px", marginBottom: "10px" }}>
							Click on a day to add a workout, task, nutrition, or automated message.
						</div>
					}
					<div style={{ fontSize: "12.8px" }}>
						<i className="fas fa-circle" style={{ color: '#5E72E4' }}/>
						<span className="mx-1">Workout |</span>
						<i className="fas fa-circle" style={{ color: '#3FCE89' }}/>
						<span className="mx-1">Task |</span>
						<i className="fas fa-circle" style={{ color: '#F66340' }}/>
						<span className="mx-1">Nutrition |</span>
						<i className="fas fa-comment-dots" style={{ color: '#43CDEF' }}/>
						<span className="mx-1">Message |</span>
						<i className="fas fa-video" style={{ color: '#eb03fc' }}/>
						<span className="mx-1">On Demand Video</span>
					</div>
					<div className={styles.ProgramGrid}>
						{weeks.map((week,i)=>(
							week.map((day,j)=>(
								( i*7+j+1 <= program.programLength ) &&
								<div 
									onDragOver={ e => {
										e.preventDefault()
										e.stopPropagation()
									}}
									onClick={()=>{
										if ( !isOwner ) return
										dispatch(updateEventLinkEditor({
											isNew:true,
											show:true,
											clickedDay:i*7+j+1,
											link:null,
											eventMeta:null,
										}))
									}}
									style={{
										cursor: isOwner ? 'pointer' : 'auto'
									}}
									className={styles.ProgramDay}
									onDrop={handleDrop.bind(null,i*7+j+1)}
								>
									<div className={styles.ProgramDayHeader}>
										Day {i*7+j+1}
									</div>
									<div className={styles.ProgramDayBody}>
										{day.map(link=>{
											switch(link.event_type){
												case 'message': return (
													<MessageLink 
														onDragStart={()=>{
															setDragData({
																link,
																day:i*7+j+1
															})
														}}
														draggable={isOwner} 
														disabled={!isOwner} 
														link={link} 
														day={i*7+j+1}
													/>
												)
												case 'workout':
												case 'task':
												case 'nutrition': return (
													<EventLink 
														onDragStart={()=>{
															setDragData({
																link,
																day:i*7+j+1
															})
														}}
														draggable={isOwner} 
														disabled={!isOwner} 
														link={link} 
														day={i*7+j+1}
													/>
												)
												case 'on_demand_video': return (
													<OnDemandVideoLink 
														onDragStart={()=>{
															setDragData({
																link,
																day:i*7+j+1
															})
														}}
														draggable={isOwner} 
														disabled={!isOwner} 
														link={link} 
														day={i*7+j+1}
													/>
												)
												default: return (
													<div>
														Unsupported Type {link.event_type}
													</div>
												)
											}
										})}
									</div>
								</div>
							))//end week.map
						))/*end weeks.map*/}
					</div>
				</div>
			}
			<UpdateAssignmentModal
				toggleShow={toggleUpdateAssignmentModal}
				show={showUpdateAssignmentModal}
				id={id}
			/>
			<EventLinkEditModal
				programId={id}
				programLength={program?.programLength}
				defaultToEvent={defaultToEvent}
				event_type={event_type}
				event_title={event_title}
				event_id={event_id}
			/>
			<AssignModal />
		</div>
	)
}

export default ProgramEditor